import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { animeArticles } from "@/mocks/animeData";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";
import SchemaOrg from "@/components/base/SchemaOrg";

export default function Article() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = animeArticles.find((a) => a.slug === slug);
  const related = animeArticles
    .filter((a) => a.slug !== slug && a.categorySlug === article?.categorySlug)
    .slice(0, 3);

  if (!article) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-800 font-heading mb-2">
            文章未找到
          </h2>
          <Link
            to="/"
            className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const ratingColor =
    article.rating >= 9
      ? "bg-primary-500"
      : article.rating >= 8
      ? "bg-accent-500"
      : article.rating >= 7
      ? "bg-secondary-500"
      : "bg-foreground-500";

  return (
    <>
      <SEOMeta
        title={`${article.title} - 好坏动漫`}
        description={article.excerpt}
        keywords={`好坏动漫,${article.category},动漫评测,${article.title}`}
        canonicalUrl={`https://haohuai.me/article/${article.slug}`}
        ogImage={article.image}
        ogType="article"
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          author: {
            "@type": "Person",
            name: article.author,
          },
          datePublished: article.publishDate,
          publisher: {
            "@type": "Organization",
            name: "好坏动漫",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://haohuai.me/article/${article.slug}`,
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://haohuai.me" },
            {
              "@type": "ListItem",
              position: 2,
              name: article.category,
              item: `https://haohuai.me/category/${article.categorySlug}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
            },
          ],
        }}
      />

      <main className="min-h-screen pt-16">
        <article className="container-site py-8">
          <Breadcrumb
            items={[
              {
                label: article.category,
                to: `/category/${article.categorySlug}`,
              },
              { label: article.title },
            ]}
          />

          <div className="mt-6 max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors duration-200 cursor-pointer"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-500">
                {article.author} · {article.publishDate}
              </span>
              <span className="text-xs text-foreground-400">
                {article.views.toLocaleString()} 次阅读
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-6">
              {article.title}
            </h1>

            <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex items-center gap-6 mb-8 p-4 bg-background-100 rounded-lg border border-background-200/70">
              <div className="text-center">
                <div
                  className={`w-16 h-16 rounded-full ${ratingColor} flex items-center justify-center mx-auto`}
                >
                  <span className="text-xl font-bold text-background-50 font-heading">
                    {article.rating}
                  </span>
                </div>
                <p className="text-xs text-foreground-500 mt-1">{t("article.rating")}</p>
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground-700 leading-relaxed">{article.excerpt}</p>
              </div>
            </div>

            <div
              className="prose max-w-none mb-10 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <h4 className="text-sm font-semibold text-primary-600 mb-3 font-heading flex items-center gap-2">
                  <i className="ri-thumb-up-line"></i>
                  {t("article.good")}
                </h4>
                <ul className="space-y-2">
                  {article.goodPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <h4 className="text-sm font-semibold text-foreground-700 mb-3 font-heading flex items-center gap-2">
                  <i className="ri-thumb-down-line"></i>
                  {t("article.bad")}
                </h4>
                <ul className="space-y-2">
                  {article.badPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-foreground-600">
                      <i className="ri-close-line text-foreground-500 mt-0.5 flex-shrink-0"></i>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="p-5 rounded-lg bg-accent-50 border border-accent-200/60 mb-10">
              <h4 className="text-sm font-semibold text-accent-700 mb-2 font-heading flex items-center gap-2">
                <i className="ri-chat-quote-line"></i>
                {t("article.verdict")}
              </h4>
              <p className="text-sm text-foreground-700 leading-relaxed">{article.verdict}</p>
            </div>

            {related.length > 0 && (
              <div className="border-t border-background-200/70 pt-10 mt-10">
                <h3 className="text-lg font-semibold text-foreground-800 mb-5 font-heading">
                  {t("article.relatedArticles")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/article/${r.slug}`}
                      className="group flex gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                      onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                    >
                      <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden">
                        <img
                          src={r.image}
                          alt={r.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 font-heading">
                          {r.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{r.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center mt-10">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-300 text-foreground-600 text-sm hover:border-primary-400 hover:text-primary-500 transition-all duration-200 cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
              >
                <i className="ri-arrow-left-line"></i>
                {t("article.backToList")}
              </Link>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}